import { UserRole } from 'types';

import { COMMUNICATOR_ROLES } from '../Palette/paletteRoles';

/**
 * ТЗ 0112 (#155) — чистая модель документов: виды, области, роли, подписи
 * полей. Без React — проверяется jest-ом.
 */

export const ISSUED_DOCUMENT_KINDS = [
  'completion',
  'participation',
  'attendance',
  'custom',
] as const;
export type IssuedDocumentKind = typeof ISSUED_DOCUMENT_KINDS[number];

export const DOCUMENT_SCOPES = ['student', 'group'] as const;
export type DocumentScope = typeof DOCUMENT_SCOPES[number];

/** i18n-ключ вида документа; незнакомый вид — `null` (рисуем как пришёл). */
export const documentKindKey = (kind?: string | null): string | null =>
  (ISSUED_DOCUMENT_KINDS as readonly string[]).includes(kind || '')
    ? `documents.kind.${kind}`
    : null;

export const documentScopeKey = (scope?: string | null): string | null =>
  (DOCUMENT_SCOPES as readonly string[]).includes(scope || '')
    ? `documents.scope.${scope}`
    : null;

/**
 * Кто выпускает документы — объединение ролей карточек студента и группы
 * (`documents/roles.py::issue_roles`). Скрытие кнопки — UX: каждый запрос
 * сервер закрывает той же ролью и областью документа.
 */
export const DOCUMENT_ISSUE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.METHODIST,
  UserRole.HRMANAGER,
];

export interface IDocumentPermissions {
  /** Шаблоны документов: создать, править, версии, выключить — Admin. */
  canEditTemplates: boolean;
  /** Выпуск и история на карточке. */
  canIssue: boolean;
  /**
   * «Отправить» документ письмом (ТЗ 0112 §4) — роли коммуникатора: письмо
   * уходит тем же путём, что «новое письмо», и ложится в переписку. Методисту
   * без коммуникатора сервер откажет — кнопки у него нет.
   */
  canSend: boolean;
}

export const documentPermissions = (
  roles: readonly string[],
): IDocumentPermissions => ({
  canEditTemplates: roles.includes(UserRole.ADMIN),
  canIssue: roles.some((role) =>
    (DOCUMENT_ISSUE_ROLES as string[]).includes(role),
  ),
  canSend: roles.some((role) =>
    (COMMUNICATOR_ROLES as string[]).includes(role),
  ),
});

export interface IDocumentField {
  code: string;
  label: string;
  labelPl: string;
}

/** Подпись поля на языке интерфейса (польская — если есть). */
export const documentFieldLabel = (
  field: IDocumentField,
  language: string,
): string =>
  (language.startsWith('pl') ? field.labelPl : field.label) ||
  field.label ||
  field.code;

/** Место выдачи по умолчанию — из последнего выпуска, где оно было. */
export const defaultIssuePlace = (
  history: ReadonlyArray<{ issuePlace?: string | null; issuedAt?: string }>,
): string => {
  const latest = [...history]
    .filter((row) => (row.issuePlace || '').trim())
    .sort((a, b) => (b.issuedAt || '').localeCompare(a.issuedAt || ''))[0];
  return latest ? (latest.issuePlace || '').trim() : '';
};

/** Коды отказов, у которых есть перевод `documents.errors.*`. */
export const DOCUMENT_ERROR_CODES = [
  'FORBIDDEN',
  'NOT_FOUND',
  'INVALID',
  'NO_CHANGES',
  'TARGET_REQUIRED',
  'SCOPE_MISMATCH',
  'TEMPLATE_INACTIVE',
  'RENDER_FAILED',
  'TOO_LARGE',
  // ТЗ 0112 §4 — отправка письмом.
  'NO_MAILBOX',
  'NOT_YOUR_MAILBOX',
  'NO_EMAIL',
  'INVALID_EMAIL',
  'INVALID_BODY',
  'GMAIL_ERROR',
] as const;

/** i18n-ключ отказа или `null` (незнакомый код — текст сервера). */
export const documentErrorKey = (code?: string | null): string | null => {
  const normalized = (code || '').toUpperCase();
  return (DOCUMENT_ERROR_CODES as readonly string[]).includes(normalized)
    ? `documents.errors.${normalized}`
    : null;
};
